import { isSSR } from '@repo/ui/src/utils/isSSR';
import { publicRuntimeConfig } from '@utils/publicRuntimeConfig';
import axios from 'axios';
import Cookies from 'js-cookie';

import { SessionResponse } from '@typings/session';
import { currentTimestamp } from '@utils/time';

const { auth } = publicRuntimeConfig;

const setStorageValue = (key: string, value: string | null | undefined) =>
  value ? localStorage.setItem(key, value) : localStorage.removeItem(key);

export const setShopTokens = (tokens: {
  shopToken?: string | null | undefined;
  basketJWT?: string | null | undefined;
}) => {
  if ('shopToken' in tokens) {
    setStorageValue('shopToken', tokens.shopToken);
  }
  if ('basketJWT' in tokens) {
    setStorageValue('basketJWT', tokens.basketJWT);
  }
};

export const getShopTokens = () => {
  if (isSSR) {
    return {};
  }
  return {
    get shopToken() {
      return localStorage?.getItem('shopToken');
    },
    get basketJWT() {
      return localStorage?.getItem('basketJWT');
    },
  };
};

export const setCustomerToken = (session: SessionResponse | null, rememberMe = true) => {
  if (!session) {
    localStorage.removeItem('sessionIdToken');
    localStorage.removeItem('sessionRefreshToken');
    localStorage.removeItem('sessionExp');
    clearBasketStorage();
    return;
  }

  localStorage.setItem('sessionIdToken', session?.idToken);
  localStorage.setItem('sessionExp', String(currentTimestamp() + (session.expiresIn ?? 0)));

  if (rememberMe && session?.refreshToken) {
    localStorage.setItem('sessionRefreshToken', session.refreshToken);
  }
};

export const clearBasketStorage = () => Cookies.remove('basketJWT');

export const getBasketToken = () => Cookies.get('basketJWT');
export const getCustomerToken = (): {
  idToken: string | null;
  refreshToken: string | null;
  exp: number;
  expired: boolean;
} => {
  try {
    const idToken = localStorage.getItem('sessionIdToken');
    const refreshToken = localStorage.getItem('sessionRefreshToken');
    const expString = localStorage.getItem('sessionExp');
    const expNum = expString ? Number(expString) : 0;
    const exp = Number.isNaN(expNum) ? 0 : expNum;
    const expired = exp - 60 <= currentTimestamp();

    return {
      idToken,
      refreshToken,
      exp,
      expired,
    };
  } catch {
    return {
      idToken: null,
      refreshToken: null,
      exp: 0,
      expired: true,
    };
  }
};

export const attemptRefresh = async (refreshToken?: string | null) => {
  if (refreshToken) {
    try {
      const { data: session } = await axios({
        url: `${auth?.url ?? ''}/tokens/refresh`,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        data: { refreshToken },
      });

      setCustomerToken(session);
      return session;
    } catch {
      /* empty */
    }
  }

  setCustomerToken(null);
};
